/**
 * OutreachTodaySection — Today's "Your outreach lists" (docs/crm-outreach-lists-plan.md §B7).
 *
 * One row per outreach list the viewer is working: name · study · "3 replied · 42 not called yet ·
 * 5 to try again today" · Work through. It sits after the Agent review queue and Callbacks on
 * purpose — those are people already waiting on a human; a list is a backlog to work down.
 *
 * NO FETCH OF ITS OWN. The rows are the `outreach` block of the worklist counts the shell already
 * polls (useNavCounts → NavCountsContext); a section that opened its own interval would be the
 * second poll against one endpoint that the shell exists to prevent. A null block (an api without
 * outreach lists, or a failed poll) hides the section rather than rendering "no lists".
 *
 * Today's "You're all caught up" card must not render above a list with people to call, so the
 * section reports its servable count upward through `onCount` — the same honesty contract as
 * FollowUpsDuePanel and CollisionTriageCard.
 *
 * Also home to `useListWorkThrough`, the one place the list pages start a list session, over the
 * §B11 contract (`useWorkQueue().startList` / `.listCounts`).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ListChecks, Loader2, Zap } from 'lucide-react';
import { useSharedNavCounts } from '../useNavCounts';
import { useWorkQueue } from '../WorkQueueContext';
import { RowLink, useRowNav } from '../components/shared';
import { listServable, nothingToWorkLine, todayListLine } from './outreachSurfaces';

/**
 * Start a "Work through" session on one list (or all of mine). When the list has nobody to call
 * right now the session is NOT entered — the caller shows `emptyLine` instead ("Everyone on your
 * list has been tried — next tries start Tue 9:00 (12 waiting)"), read from the context's
 * listCounts on the render after startList resolves, which is when the context has it.
 */
export function useListWorkThrough() {
  const wq = useWorkQueue();
  const startList = wq.startList;
  const [busyFor, setBusyFor] = useState<string | null>(null);
  const [emptyFor, setEmptyFor] = useState<string | null>(null);
  const [errorFor, setErrorFor] = useState<string | null>(null);

  const start = useCallback(async (listId: string) => {
    setEmptyFor(null);
    setErrorFor(null);
    setBusyFor(listId);
    try {
      // startList resolves 0 (never throws) when nobody is servable OR the fetch failed; the
      // context's listCounts tells the two apart (null = the fetch failed).
      const n = await startList(listId);
      if (n === 0) setEmptyFor(listId);
    } catch {
      setErrorFor(listId);
    } finally {
      setBusyFor(null);
    }
  }, [startList]);

  return {
    /** A session is already running (the bar is up) — Today hides its buttons then too. */
    sessionActive: wq.active,
    starting: wq.starting || busyFor !== null,
    busyFor,
    start,
    emptyFor,
    emptyLine: emptyFor ? nothingToWorkLine(wq.listCounts) : null,
    errorFor,
    errorLine: errorFor ? "Couldn't start working through this list. Please try again." : null,
  };
}

const workBtnCls =
  'inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 sm:min-h-0';

export default function OutreachTodaySection({ onCount }: { onCount?: (n: number) => void }) {
  const navigate = useNavigate();
  const rowNav = useRowNav();
  const { outreach } = useSharedNavCounts();
  const work = useListWorkThrough();

  // Only lists I still have people on — a list whose share of mine is all finished is history,
  // not work, and it lives on the Outreach lists page.
  const lists = (outreach?.lists || []).filter((l) => l.assigned_open > 0);
  const servable = lists.length ? Math.max(outreach?.servable || 0, lists.reduce((a, l) => a + listServable(l), 0)) : 0;

  useEffect(() => { onCount?.(servable); }, [servable, onCount]);

  if (!outreach || lists.length === 0) return null;

  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <ListChecks className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Your outreach lists</h2>
        <span className="text-sm text-gray-500 dark:text-gray-400" title="People on your lists you can work right now">
          ({servable.toLocaleString()})
        </span>
        <span className="ml-auto hidden text-xs text-gray-400 sm:block">Lists a manager gave you — work them top to bottom</span>
      </div>
      <div className="divide-y divide-gray-100 px-4 dark:divide-gray-700">
        {lists.map((l) => {
          const url = `/crm/outreach/${l.id}`;
          // Offered on every list with open people, not only when replied/untouched/retry are
          // non-zero: the row's counts leave out due callbacks and agent hand-backs, which the
          // list session does serve. An empty start explains itself (emptyLine) anyway.
          const canWork = l.assigned_open > 0;
          const busy = work.busyFor === l.id;
          const showWork = !work.sessionActive && canWork;
          return (
            <div key={l.id} className="py-3">
              <div
                role="button"
                tabIndex={0}
                {...rowNav(url)}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(url); }}
                className="-mx-4 flex cursor-pointer flex-wrap items-center gap-x-3 gap-y-2 px-4 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20"
              >
                {/* Phone: the name and counts take the whole first line and the
                    Work through button drops under them, right-aligned, instead
                    of squeezing the list's name to three words beside it. */}
                <div className={`min-w-0 flex-1 ${showWork ? 'max-sm:basis-full' : ''}`}>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <RowLink to={url} title={l.name} className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {l.name}
                    </RowLink>
                    {l.study_title && (
                      <span className="min-w-0 truncate text-xs text-gray-500 dark:text-gray-400">{l.study_title}</span>
                    )}
                  </div>
                  <p className={`mt-0.5 text-xs ${l.replied > 0 ? 'font-medium text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>
                    {todayListLine(l)}
                  </p>
                </div>
                {showWork ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); work.start(l.id); }}
                    onKeyDown={(e) => e.stopPropagation()}
                    disabled={work.starting}
                    aria-label={`Work through ${l.name}`}
                    className={`${workBtnCls} max-sm:ml-auto`}
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Zap className="h-4 w-4" aria-hidden="true" />}
                    Work through
                  </button>
                ) : (
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-600" aria-hidden="true" />
                )}
              </div>
              {(work.emptyFor === l.id || work.errorFor === l.id) && (
                <p role="status" className="mt-1.5 text-xs text-gray-600 dark:text-gray-300">
                  {work.emptyFor === l.id ? work.emptyLine : work.errorLine}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
